import React, { useState, useCallback, useEffect } from "react";
import { withFormik } from 'formik';
import * as Yup from "yup";
import moment from "moment"
import { useTranslation } from 'react-i18next';
import { useStoreState, useStoreActions } from 'easy-peasy';

import { Button, Typography, Spinner } from "stories/components";
import { Container, Row, Col, FormContainer, Card, CardBody } from "stories/layout";
import { Input, SelectMulti, Toggle, Radio, DatePicker } from "stories/forms";
import { useNotification } from "stories/components/Notification"

import DerivativeTypeInput from "components/Forms/DerivativeTypeInput/DerivativeTypeInput"
import OptionRightInput from "components/Forms/OptionRightInput/OptionRightInput"
import OptionAssetTypeInput from "components/Forms/OptionAssetTypeInput/OptionAssetTypeInput"
import OptionRoleInput from "components/Forms/OptionRoleInput/OptionRoleInput"
import OptionStateInput from "components/Forms/OptionStateInput/OptionStateInput"
import StockSelect from "components/Forms/StockSelect/StockSelect"
import StockPrivateSelect from "components/Forms/StockPrivateSelect/StockPrivateSelect"
import StockMarketSelect from "components/Forms/StockMarketSelect/StockMarketSelect"
import PrivateInvestmentFundSelect from "components/Forms/PrivateInvestmentFundSelect/PrivateInvestmentFundSelect"
import CurrencySelect from "components/Forms/CurrencySelect/CurrencySelect"
import FileDownloadButton from "components/Buttons/FileDownloadButton/FileDownloadButton"
import { round, DERIVATIVE_PRICE_DECIMALS, padDerivativePrice } from "services/helpers"

import { 
  httpGetOption, 
  httpCreateOption, 
  httpUpdateOption, 
  httpDeleteOption, 
  httpGetPreOptions,
} from "services/options"
import { loadSourceFile } from "views/app/OptionEdit/sourceFile"
import DerivativeTerminationPanel from "views/app/OptionEdit/DerivativeTerminationPanel"
import TerminationSettlementFields from "views/app/OptionEdit/TerminationSettlementFields"
import { buildOptionApiPayload, buildPartialTerminationRows, resolveLegacyTerminationSettlement } from "views/app/OptionEdit/derivativeTerminationUtils"
import { getOptionEditLock, shouldRevertParentAfterDelete } from "views/app/OptionEdit/terminationLocks"
import { useAppDialog } from "components/AppDialog/useAppDialog"
import "./OptionEdit.css"
import "./derivativeAlignedRow.css"

const defaultValues = {
  id: null,
  derivative_type: 2,
  asset_type: 2,
  option_right: "2",
  spot_price_manual: 0.0,
  strike_price: padDerivativePrice(0),
  premium: padDerivativePrice(0),
  premium_total: padDerivativePrice(0),
  other_expenses: padDerivativePrice(0),
  lot_size: 1,
  lot_amount: 1,
  currency_1: "EUR",
  currency_2: "USD",
  exercise_price: padDerivativePrice(0),
  termination_price: padDerivativePrice(0),
  termination_fee: 0.0,
  receives: 0.0,
}

const DECIMAL_PLACES = 6;
const PRICE_DECIMAL_PLACES = DERIVATIVE_PRICE_DECIMALS;

const parseMoneyValue = (value, places) => {
  const parsed = parseFloat(String(value ?? "").replace(",", "."));
  return Number.isFinite(parsed) ? round(parsed, places) : 0;
};

const getOptionFieldErrorLabel = (t, field, fallbackMessage) => {
  const labels = {
    asset_id: t("validation_option_asset_missing", "Osake puuttuu"),
    asset_name: t("option_edit_name", "Nimi"),
    owner_name: t("option_edit_owner", "Ostaja"),
    writer_name: t("option_edit_writer", "Myyjä"),
    start_date: t("option_edit_start_date", "Alkupäivä"),
    end_date: t("option_edit_end_date", "Eräpäivä"),
    strike_price: t("option_edit_strike_price", "Toteutushinta"),
    premium: t("option_edit_premium", "Preemio / kpl"),
    premium_total: t("option_edit_premium_total", "Preemio yhteensä"),
    other_expenses: t("option_edit_other_expenses", "Muut kulut"),
    exercise_price: t("option_edit_exercise_price", "Lopullinen toteutushinta"),
    termination_price: t("option_edit_termination_unit_price", "Terminoitu kappalehinta"),
    receives: t("option_edit_termination_total", "Terminoitu kokonaissumma"),
  };
  if (labels[field]) {
    return `${labels[field]}: ${fallbackMessage}`;
  }
  return `${field}: ${fallbackMessage}`;
};

const OptionEdit = (props) => {
  const { dialog, confirm } = useAppDialog();
  const [terminationLock, setTerminationLock] = useState(false);
  const [terminationPanelOpen, setTerminationPanelOpen] = useState(false);

  const {
     values,
     touched,
     setTouched,
     setFieldTouched,
     errors,
     setErrors,
     handleChange,
     handleBlur,
     handleSubmit,
     setFieldValue,
     setFieldError,
     validateField,
     isValid,
     isTouched,
     isSubmitting,
     validateForm,
     preSetValues
   } = props;
   
   const {
      t,
      history,
      stockMarket,
      setStockMarket,
      getBasicDataObjects,
      objectId,
      portfolio,
   } = props;
   
   const getFormikInputProps = useCallback(
    name => ({
      name,
      value: values[name],
      error: Boolean(touched[name] && errors[name]),
      errorText: touched[name]
        ? (Array.isArray(errors[name]) ? errors[name][0] : errors[name])
        : "",
      onBlur: handleBlur,
      onChange: handleChange
    }),
    [values, errors, touched, handleBlur, handleChange]
  );

  const clearAssetIdError = useCallback(() => {
    if (errors?.asset_id) {
      setFieldError("asset_id", undefined);
    }
    if (errors?.general) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.general;
        delete next.asset_id;
        return next;
      });
    }
  }, [errors?.asset_id, errors?.general, setFieldError, setErrors]);
  
  const handleAssetSelected = useCallback(
    (params) => {
      if (params?.id) {
        setFieldValue("asset_id", params.id);
        setFieldTouched("asset_id", true, false);
        clearAssetIdError();
        validateField("asset_id");
      }
      if (params?.name) {
        setFieldValue("asset_name", params.name);
      }
    },
    [setFieldValue, setFieldTouched, clearAssetIdError, validateField]
  );
  
  const handlePremiumChange = ({target}, child) => {
    const premium = target.value.toString().replace(",",".")
    setFieldValue("premium", premium)
    if (values?.quantity) {
      const premium_total = round(parseFloat(premium) * parseInt(values?.quantity), PRICE_DECIMAL_PLACES)
      setFieldValue("premium_total", padDerivativePrice(premium_total))
    }
  }
  
  const handlePremiumTotalChange = ({target}, child) => {
    const premium_total = target.value.toString().replace(",",".")
    setFieldValue("premium_total", premium_total)
    if (values?.quantity) {
      const premium = round(parseFloat(premium_total) / parseInt(values?.quantity), PRICE_DECIMAL_PLACES)
      setFieldValue("premium", padDerivativePrice(premium))
    }
  }
  
  useEffect(() => {
    const lot_size = parseInt(values?.lot_size)
    const lot_amount = parseInt(values?.lot_amount)
    const quantity = lot_size * lot_amount
    setFieldValue("quantity", quantity)
  }, [values?.lot_size, values?.lot_amount])
  
  useEffect(() => {
    if (values?.quantity && values?.premium)  {
      const premium_total = parseInt(values?.quantity) * parseFloat(values?.premium)
      setFieldValue("premium_total", premium_total)
    }
  }, [values?.quantity])
  
  const resolveStockPublic = (params) => {
    handleAssetSelected(params);
  };
  
  const resolveStockPrivate = (params) => {
    handleAssetSelected(params);
  };
  
  const resolvePrivateInvestmentFund = (params) => {
    handleAssetSelected(params);
  };
  
  const onSubmit = (e) => {
    e.preventDefault();

    const showValidationErrors = (validationErrors) => {
      setTouched(validationErrors);
      const fieldMessages = Object.entries(validationErrors)
        .map(([field, message]) => getOptionFieldErrorLabel(t, field, message))
        .join("\n");
      const generalMsg = t("Please check the form errors", "Tarkista lomakkeen virheet");
      setErrors({
        ...validationErrors,
        general: generalMsg,
      });
      props.notify({
        title: t("error_title", "Virhe"),
        message: fieldMessages || generalMsg,
        type: "danger",
      });
    };

    // Terminointi: lomake on lukittu/piilotettu, lähetetään suoraan backendiin.
    if (terminationLock) {
      handleSubmit();
      return;
    }

    validateForm().then((res) => {
      if (Object.keys(res).length === 0) {
        handleSubmit();
      } else {
        showValidationErrors(res);
      }
    });
  };

  
  const handleCancel = () => {
    props.handleReject()
  }
  
  const handleConfirm = () => {
    /*
    httpConfirmPurchaseOrderShipping(values?.id).then(res => {
      handleSubmit()
    })
    */
  }
  
  const handleStockMarketChange = ({target}) => {
    setStockMarket(target.value)
  }
  
  useEffect(() => {
    getBasicDataObjects({stock_market:stockMarket})
  }, [stockMarket])
  
  const stocks = useStoreState((state) => state.stocks?.objects);
  const privateStocks = useStoreState((state) => state.privateStocks?.objects);
  const private_investment_funds = useStoreState((state) => state.privateInvestmentFunds?.objects);

  
  
  const getAssetTypeSelect = useCallback(() => {
    // Apufunktio joka hakee arvon oikeasta listasta
    const getAssetValue = () => {
      if (!values?.asset_id) return null;
      
      let asset = null;
      if (values?.asset_type == 2) {
        asset = stocks?.find(s => s.id === values.asset_id);
      } 
      else if (values?.asset_type == 6) {
        asset = privateStocks?.find(s => s.id === values.asset_id);
      }
      else if (values?.asset_type == 10) {
        asset = private_investment_funds?.find(s => s.id === values.asset_id);
      }

      if (!asset) {
        if (values?.asset_name) {
          return {
            value: values.asset_id,
            label: values.asset_name,
          };
        }
        return null;
      }
      return {
        value: asset.id,
        label: asset.name,
      };
    };

    if (values?.asset_type == 2) {
      return (
        <Row className="derivative-aligned-row">
          <Col xs="12" md="5" lg="4" className="mb-3 mb-md-0">
            <StockMarketSelect
              onChange={handleStockMarketChange}
              value={stockMarket}
              showPrivate={false}
            />
          </Col>
          <Col xs="12" md="7" lg="8">
            <StockSelect
              {...getFormikInputProps("asset_id")}
              value={getAssetValue()}
              label={t("option_edit_asset_stock", "Stock")}
              setFieldValue={setFieldValue}
              setFieldValueReturnId={true}
              handleResolve={resolveStockPublic}
              required={true}
            />
          </Col>
        </Row>
      )
    }
    else if (values?.asset_type == 6) {
      return (
        <StockPrivateSelect
          {...getFormikInputProps("asset_id")}
          label={t("option_edit_private_stock", "Stock")}
          setFieldValue={setFieldValue}
          handleResolve={resolveStockPrivate}
        />
      )
    }
    else if (values?.asset_type == 10) {
      return (
        
        <PrivateInvestmentFundSelect
          {...getFormikInputProps("asset_id")}
          value={getAssetValue()}
          label={t("option_edit_private_investment_fund", "Investment fund")}
          setFieldValue={setFieldValue}
          handleResolve={resolvePrivateInvestmentFund}
          />
      )
    }
    else if (values?.asset_type == 12) {
      return (
        <>
          <CurrencySelect
            {...getFormikInputProps("currency_1")} 
            label={t("option_edit_currency_1", "Currency")}
            required
            disabled
          />
          <CurrencySelect
            {...getFormikInputProps("currency_2")} 
            label={t("option_edit_currency_2", "Currency")}
            required
          />
        </>
      )
    }
    else {
      return null;// t("No data available","Ei dataa saatavilla")
    }
  }, [values?.asset_type, stockMarket, values?.asset_id, values?.asset_name, stocks, privateStocks, private_investment_funds])
  
  useEffect(() => {
    if (values?.asset_id) {
      clearAssetIdError();
    }
  }, [values?.asset_id, clearAssetIdError]);

  useEffect(() => {
    if (terminationLock && errors?.asset_id) {
      setTerminationLock(false);
      setTerminationPanelOpen(false);
    }
  }, [errors?.asset_id, terminationLock]);
  
  useEffect(() => {
    if (values?.asset_type == 12) {
      setFieldValue("asset_name", `${values?.currency_1}-${values?.currency_2}`)
    }
    
  }, [values?.asset_type, values?.currency_1, values?.currency_2])

  // A new contract takes the portfolio currency. An existing row keeps its own.
  useEffect(() => {
    if (objectId && parseInt(objectId, 10) > -1) return;
    const currency = portfolio?.currency_label;
    if (!currency || values?.currency_1 === currency) return;
    setFieldValue("currency_1", currency);
  }, [portfolio?.currency_label, objectId, values?.currency_1]);
  
  const lineageLock = getOptionEditLock(values, props.options || []);

  const handleDelete = () => {
    if (!lineageLock.canDelete) return;
    confirm(t("are_you_sure_window_confirm","Are you sure?")).then((result) => {
      if (result) {
        httpDeleteOption(values?.id).then(() => {
          const parentId = values?.parent_option || values?.parent_option_id;
          if (parentId && shouldRevertParentAfterDelete(values, props.options || [])) {
            return httpUpdateOption(parentId, { option_state: 2, option_state_date: null });
          }
          return null;
        }).then(() => {
          props.handleResolve()
        })
      }
    });
  }
  
  useEffect(() => {
    if (!objectId){
      if (Number(values?.asset_type) === 12) {
        setFieldValue("asset_id", 0);
      } else {
        setFieldValue("asset_id", null);
        setFieldValue("asset_name", "");
      }
    }
  }, [values?.asset_type]);

  useEffect(() => {
    setTerminationLock(false);
    setTerminationPanelOpen(false);
  }, [objectId]);

  const canShowTerminateButton = values?.id && Number(values?.option_state) === 2 && !terminationLock;

  const handleTerminationConfirm = (result) => {
    setTerminationLock(true);
    setTerminationPanelOpen(false);

    // Persist the result so handleSubmit can auto-create the remaining contract.
    if (typeof props.onTerminationResult === "function") {
      props.onTerminationResult(result);
    }

    setFieldValue("option_state_date", result.terminated.option_state_date);
    setFieldValue("termination_price", result.terminated.termination_price ?? "");
    setFieldValue("receives", result.terminated.receives ?? "");
    setFieldValue("termination_fee", result.terminated.termination_fee ?? result.terminated.termination_price ?? "");
  };

  const handleTerminationUndo = () => {
    setTerminationLock(false);
    setTerminationPanelOpen(false);
    if (typeof props.onTerminationResult === "function") {
      props.onTerminationResult(null);
    }
  };

  const formLocked = terminationLock || lineageLock.structuralLocked;
  const lockProps = formLocked ? { disabled: true, readOnly: true } : {};
  const showFooter = !(terminationPanelOpen && !terminationLock);
  
  return (
    <>
    {dialog}
    <div className="px-3 pt-2">
      <DerivativeTerminationPanel
        option={values}
        disabled={isSubmitting}
        stockMarket={stockMarket}
        onConfirm={handleTerminationConfirm}
        onResultChange={props.onTerminationResult}
        isOpen={terminationPanelOpen}
        onIsOpenChange={setTerminationPanelOpen}
        terminationConfirmed={terminationLock}
      />

      {!terminationLock && (
      <Row className="pb-3 pt-2">
        <Col xs={12} md={6} className="border-right px-4 pt-2">

          <Row className="derivative-aligned-row">
            <Col xs="12" md="6" className="mb-3">
              <DerivativeTypeInput
                {...getFormikInputProps("derivative_type")}
                label={t("option_edit_derivative_type", "Derivative type")}
                allOption={false}
                disabled={formLocked}
              />
            </Col>
            <Col xs="12" md="6" className="mb-3">
              <OptionAssetTypeInput
                {...getFormikInputProps("asset_type")}
                label={t("option_edit_asset_type", "Asset type")}
                allOption={false}
                disabled={formLocked}
              />
            </Col>
          </Row>

          <Row>
            <Col className="mb-3">
            { terminationLock ? (
              <Row className="derivative-aligned-row">
                {values?.asset_type == 2 && (
                  <Col xs="12" md="5" className="mb-3">
                    <Input
                      name="locked_stock_market"
                      type="text"
                      label={t("derivative_termination_stock_market", "Pörssi")}
                      value={stockMarket}
                      disabled
                      readOnly
                    />
                  </Col>
                )}
                <Col xs="12" md={values?.asset_type == 2 ? 7 : 12} className="mb-3">
                  <Input
                    name="locked_asset_name"
                    type="text"
                    label={t("option_edit_asset_stock", "Osake")}
                    value={values?.asset_name}
                    disabled
                    readOnly
                  />
                </Col>
              </Row>
            ) : getAssetTypeSelect() }
            </Col>
          </Row>
          
          <Row>
            <Col className="mb-3">
              <Input
                {...getFormikInputProps("asset_name")}
                label={t("option_edit_name", "Name")}
                required
                {...lockProps}
              />
            </Col>
          </Row>
          
          <Row>
            <Col className="mb-3">
              <Input
                {...getFormikInputProps("trade_id")}
                label={t("option_edit_trade_id", "Trade id")}
                {...lockProps}
              />
            </Col>
          </Row>
          {values?.derivative_type == 2 && 
            <Row>
            <Col className="mb-3">
              <OptionRightInput
                {...getFormikInputProps("option_right")}
                label={t("option_edit_right", "Right")}
                allOption={false}
                disabled={formLocked}
                />
            </Col>
          </Row>
              }
          
          <Row>
            <Col className="mb-3">
              <Input
                {...getFormikInputProps("owner_name")}
                label={t("option_edit_owner", "Owner/Buyer")}
                required
                {...lockProps}
              />
            </Col>
          </Row>
          
          <Row>
            <Col className="mb-3">
              <Input
                {...getFormikInputProps("writer_name")}
                label={t("option_edit_writer", "Writer/Seller")}
                required
                {...lockProps}
              />
            </Col>
          </Row>
          
          <Row>
            <Col className="mb-3">
              <OptionRoleInput
                {...getFormikInputProps("role")}
                label={t("option_edit_role", "Role")}
                allOption={false}
                disabled={formLocked}
              />
            </Col>
          </Row>

          </Col>
        <Col className="px-4 pt-2">
          <Row className="derivative-aligned-row">
            <Col xs="12" md="6" className="mb-3">
              <Input
                {...getFormikInputProps("lot_size")}
                label={t("option_edit_lot_size", "Lot size")}
                type="number"
                min={1}
                {...lockProps}
              />
            </Col>
            <Col xs="12" md="6" className="mb-3">
              <Input
                {...getFormikInputProps("lot_amount")}
                label={t("option_edit_lot_amount", "Lots amount")}
                type="number"
                min={1}
                {...lockProps}
              />
            </Col>
          </Row>
          <Row>
            <Col className="mb-3">
              <Input
                {...getFormikInputProps("quantity")}
                label={t("option_edit_quantity", "Quantity")}
                type="number"
                min={0}
                disabled
              />
            </Col>
          </Row>


          
          <Row className="derivative-aligned-row">
            <Col xs="12" md="6" className="mb-3">
              <DatePicker
                {...getFormikInputProps("start_date")}
                label={t("option_edit_start_date", "Start date")}
                className="form-control"
                selected={values?.start_date}
                name="start_date"
                onChange={(date) => setFieldValue("start_date", date)}
                showYearDropdown
                dateFormat="d.M.yyyy"
                value={values?.start_date}
                required
              />
            </Col>
            <Col xs="12" md="6" className="mb-3">
              <DatePicker
                {...getFormikInputProps("end_date")}
                label={t("option_edit_end_date", "Expiry date")}
                className="form-control"
                selected={values?.end_date}
                name="end_date"
                onChange={(date) => setFieldValue("end_date", date)}
                showYearDropdown
                dateFormat="d.M.yyyy"
                value={values?.end_date}
                required
                disabled={formLocked}
              />
            </Col>
          </Row>
          
          <Row className="derivative-aligned-row">
            <Col xs="12" md="6" className="mb-3">
              <Input
                {...getFormikInputProps("premium")}
                onChange={handlePremiumChange}
                label={t("option_edit_premium", "Premium / share")}
                type="decimal"
                decimalPlaces={PRICE_DECIMAL_PLACES}
              />
            </Col>
            <Col xs="12" md="6" className="mb-3">
              <Input
                {...getFormikInputProps("premium_total")}
                onChange={handlePremiumTotalChange}
                label={t("option_edit_premium_total", "Premium total")}
                type="decimal"
                decimalPlaces={PRICE_DECIMAL_PLACES}
              />
            </Col>
          </Row>
          
          <Row>
            <Col className="mb-3">
              <Input
                {...getFormikInputProps("other_expenses")}
                label={t("option_edit_other_expenses", "Muut kulut")}
                type="decimal"
                decimalPlaces={PRICE_DECIMAL_PLACES}
              />
            </Col>
          </Row>
          
          <Row>
            <Col className="mb-3">
              <Input
                {...getFormikInputProps("strike_price")}
                label={t("option_edit_strike_price", "Strike price")}
                type="decimal"
                decimalPlaces={PRICE_DECIMAL_PLACES}
              />
            </Col>
          </Row>
          
          {/*
          <Row>
            <Col className="mb-3">
              <Input
                {...getFormikInputProps("spot_price_manual")}
                label={t("option_edit_spot_price", "Spot price (Ajankohtainen hinta)")}
                type="number"
                step="0.01"
                min={0}
                helpText={t("If live spot price not available")}
              />
            </Col>
          </Row>
          */}
          
          <Row>
            <Col className="mb-3">
              <OptionStateInput
                {...getFormikInputProps("option_state")}
                label={t("option_edit_state", "State")}
                allOption={false}
                disabled={formLocked}
              />
            </Col>
          </Row>
          
          { values?.option_state && values?.option_state > 2 ? (
            <Row>
              <Col className="mb-3">
                <DatePicker
                  label={t("option_edit_state_date", "State date")}
                  className="form-control"
                  selected={values?.option_state_date}
                  name="option_state_date"
                  onChange={(date) => setFieldValue("option_state_date", date)}
                  showYearDropdown
                  dateFormat="d.M.yyyy"
                  value={values?.option_state_date}
                />
              </Col>
            </Row>
          ) : null }
          
          { values?.option_state && values?.option_state == 3 ? (
            <Row>
              <Col className="mb-3">
                <Input
                  {...getFormikInputProps("exercise_price")}
                  label={t("option_edit_exercise_price", "Exercise price")}
                  type="decimal"
                  decimalPlaces={PRICE_DECIMAL_PLACES}
                />
              </Col>
            </Row>
          ) : null}
          
          { values?.option_state && (values?.option_state == 5 || values?.option_state == "5") ? (
            <TerminationSettlementFields
              quantity={values?.quantity}
              unitPrice={values?.termination_price}
              onUnitPriceChange={(value) => {
                setFieldValue("termination_price", value);
                setFieldValue("termination_fee", value);
              }}
              total={values?.receives}
              onTotalChange={(value) => setFieldValue("receives", value)}
              strikePrice={values?.strike_price}
              role={values?.role}
              optionRight={values?.option_right}
              derivativeType={values?.derivative_type}
            />
          ) : null}
          
          <Row>
            <Col className="mb-3">
              <Input
                {...getFormikInputProps("notes")}
                label={t("option_edit_notes", "Notes")}
                type="textarea"
              />
            </Col>
          </Row>
          
        </Col>

      </Row>
      )}

      {values?.source_file?.id && !terminationLock ? (
        <div className="px-4 pb-3 d-flex align-items-center">
          <div className="mr-2 text-truncate">
            <Typography variant="small" className="text-muted mb-0">
              {t("option_edit_source_file", "Lähdetiedosto")}
            </Typography>
            <Typography className="mb-0 text-truncate" title={values.source_file.filename}>
              {values.source_file.filename}
            </Typography>
          </div>
          <FileDownloadButton
            showPreview
            fileId={values.source_file.id}
            fileName={values.source_file.filename}
          />
        </div>
      ) : null}
      
      {showFooter && (
      <Row className="bg-lighter pt-3 rounded-bottom option-edit-footer">
        {terminationLock ? (
          <>
            <Col className="mb-3">
              <Button
                variant="secondary"
                className="sk-modal-dismiss"
                disabled={isSubmitting}
                onClick={handleTerminationUndo}
              >
                {t("derivative_termination_cancel", "Peruuta")}
              </Button>
            </Col>
            <Col className="mb-3 d-flex justify-content-end align-items-center flex-wrap gap-2">
              { !preSetValues?.state_delivery && (
                <Button variant="primary" disabled={isSubmitting} onClick={onSubmit}>
                  {t("derivative_termination_save", "Tallenna terminointi")}
                </Button>
              )}
              { Boolean(errors["general"]) && <Typography className="text-danger">{errors["general"]}</Typography> }
            </Col>
          </>
        ) : (
          <>
        <Col className="mb-3">
          <Button onClick={handleCancel} variant="secondary" className="sk-modal-dismiss">{t("Close","Sulje")}</Button>
        </Col>
        { values?.id && lineageLock.canDelete ? (
        <Col className="mb-3 text-center">
          <Button onClick={handleDelete} variant="danger">{t("delete_button","Delete")}</Button>
        </Col>
        ) : null }
        <Col className="mb-3 d-flex justify-content-end align-items-center option-edit-footer-actions">
          {canShowTerminateButton && (
            <Button
              variant="secondary"
              disabled={isSubmitting}
              onClick={() => setTerminationPanelOpen(true)}
            >
              {t("derivative_termination_terminate", "Terminoi")}
            </Button>
          )}
          { !preSetValues?.state_delivery && !lineageLock.structuralLocked && (
            <Button variant="primary" disabled={isSubmitting} onClick={onSubmit}>
              {t("Save", "Tallenna")}
            </Button>
          )}
          
          { Boolean(errors["general"]) && <Typography className="text-danger">{errors["general"]}</Typography> }
        </Col>
          </>
        )}
      </Row>
      )}
    </div>
        
    </>
  );
}

const OptionEditFormik = withFormik({
    enableReinitialize: true,
    validateOnMount: true,
    mapPropsToValues: props => {
      const { preSetValues } = props;
      if ( preSetValues) {
        return preSetValues;
      } else {
        return {
          ...defaultValues
        }
      }
    },
    validationSchema: props => {
      const {t} = props;
      const required = t("validation_required_field","The field is required");
      const assetRequired = t("validation_option_asset_missing", "Osake puuttuu");
      return Yup.object().shape({
        asset_name: Yup.string().required(required).nullable(),
        asset_type: Yup.number().nullable(),
        asset_id: Yup.number().nullable().when("asset_type", {
          is: (value) => Number(value) !== 12,
          then: Yup.number().required(assetRequired).nullable(),
          otherwise: Yup.number().nullable(),
        }),
        end_date: Yup.string().required(required).nullable(),
        owner_name: Yup.string().required(required).nullable(),
        start_date: Yup.string().required(required).nullable(),
        writer_name: Yup.string().required(required).nullable(),
      });
    },
    handleSubmit: (values, { setSubmitting, setErrors, props }) => {
      const {t, portfolio} = props;

      // Normalise a Date (or date string) to "YYYY-MM-DD 12:00:00Z".
      const formatDateField = (date) => {
        if (!date) return null;
        const d = date instanceof Date ? date : new Date(date);
        if (isNaN(d.getTime())) return null;
        const n = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0);
        return n.toISOString().split("T")[0] + " 12:00:00Z";
      };

      const values_premium = values?.premium ? round(parseFloat(values?.premium.toString().replace(",",".")), PRICE_DECIMAL_PLACES) : 0;
      const values_premium_total = values?.premium_total ? round(parseFloat(values?.premium_total.toString().replace(",",".")), PRICE_DECIMAL_PLACES) : 0;
      const values_other_expenses = values?.other_expenses ? round(parseFloat(values?.other_expenses.toString().replace(",",".")), PRICE_DECIMAL_PLACES) : 0;
      const terminatedSettlement = props.terminationResult?.terminated;
      const pickSettlementValue = (resultValue, formValue) => (
        resultValue != null && resultValue !== "" ? resultValue : formValue
      );
      const values_termination_price = parseMoneyValue(
        pickSettlementValue(terminatedSettlement?.termination_price, values?.termination_price),
        PRICE_DECIMAL_PLACES
      );
      const values_receives = parseMoneyValue(
        pickSettlementValue(terminatedSettlement?.receives, values?.receives),
        PRICE_DECIMAL_PLACES
      );
      const values_termination_fee = parseMoneyValue(
        pickSettlementValue(terminatedSettlement?.termination_fee, values?.termination_fee),
        DECIMAL_PLACES
      ) || values_termination_price;
      const values_strike_price = values?.strike_price ? round(parseFloat(values?.strike_price.toString().replace(",",".")), PRICE_DECIMAL_PLACES) : 0;
      const values_exercise_price = values?.exercise_price ? round(parseFloat(values?.exercise_price.toString().replace(",",".")), PRICE_DECIMAL_PLACES) : 0;

      const formatExerciseDate = (date) => moment(date).format();

      let data = buildOptionApiPayload(values, {
        portfolioId: portfolio?.id,
        formatDateField,
        formatExerciseDate,
      });
      data = {
        ...data,
        premium: values_premium,
        premium_total: values_premium_total,
        other_expenses: values_other_expenses,
        termination_fee: values_termination_fee || values_termination_price,
        termination_price: values_termination_price,
        receives: values_receives,
        strike_price: values_strike_price,
        exercise_price: values_exercise_price,
      };

      const handleError = (error) => {
        setSubmitting(false);
        let message = t("error_unknown", "Unknown error. Please try again later or contact support.");
        if (error?.data?.detail) {
          message = error.data.detail;
          setErrors({ general: message });
        } else if (error?.data) {
          message = typeof error.data === "string"
            ? error.data
            : Object.entries(error.data)
                .map(([field, value]) => {
                  const text = Array.isArray(value) ? value.join(", ") : value;
                  return getOptionFieldErrorLabel(t, field, text);
                })
                .join("\n");
          setErrors({ ...error.data, general: message });
        } else {
          setErrors({ general: message });
        }
        props.notify({
          title: t("derivative_title", "Derivative"),
          message,
          type: "danger",
        });
      };

      if (values?.id && props.terminationResult) {
        const rows = buildPartialTerminationRows(values, {
          terminationLotSize: props.terminationResult.terminatedLotSize,
          eventDate: props.terminationResult.terminated?.option_state_date,
          remainingTradeId: props.terminationResult.remaining?.trade_id,
          settlement: {
            termination_price: values_termination_price,
            receives: values_receives,
            termination_fee: values_termination_fee,
          },
          portfolioId: portfolio?.id,
          formatDateField,
          formatExerciseDate,
        });
        const creates = [rows.terminatedCreate, rows.remainingCreate].filter(Boolean);
        let chain = Promise.resolve();
        creates.forEach((payload) => {
          chain = chain.then(() => httpCreateOption(payload));
        });
        chain
          .then(() => httpUpdateOption(values.id, rows.originalPatch))
          .then(() => {
            if (typeof props.onTerminationResult === "function") {
              props.onTerminationResult(null);
            }
            setSubmitting(false);
            props.notify({ title: t("derivative_title","Derivative"), message: t("updated_message","Updated") });
            props.handleResolve();
          })
          .catch(handleError);
      }
      else if (values?.id) {
        httpUpdateOption(values.id, data)
          .then((res) => res)
          .then(() => {
            if (typeof props.onTerminationResult === "function") {
              props.onTerminationResult(null);
            }
            setSubmitting(false);
            props.notify({ title: t("derivative_title","Derivative"), message: t("updated_message","Updated") });
            props.handleResolve();
          })
          .catch(handleError);
      }
      else {
        httpCreateOption(data).then(res => {
          setSubmitting(false);
          props.notify({ title:t("derivative_title","Derivative"), message:t("created_message","Created")})
          props.handleResolve()
        }, handleError);
      }
    },
    displayName: "BasicForm"
  
})(OptionEdit)
    
    
const OptionEditView = ({history, objectId, modalOpen, ...rest}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [preSetValues, setPreSetValues] = useState();
  const [terminationResult, setTerminationResult] = useState(null);
  const { notify } = useNotification();
  const [stockMarket, setStockMarket] = useState("XHEL");
  
  // Store: States
  const portfolio = useStoreState((state) => state.portfolio?.portfolio);
  const options = useStoreState((state) => state.options?.options);
  const stocks = useStoreState((state) => state.stocks?.objects);
  const privateStocks = useStoreState((state) => state.privateStocks?.objects);
  const private_investment_funds = useStoreState((state) => state.privateInvestmentFunds?.objects);
  
  // Store: Actions
  const getBasicDataObjects = useStoreActions((actions) => actions.stocks.getObjects);
  
  const getObject = (id) => {
    
    setLoading(true)
    
    httpGetOption(id).then(res => {
      const data = res?.data;
      const legacySettlement = resolveLegacyTerminationSettlement(data);
      return loadSourceFile(data, httpGetPreOptions).catch(() => null).then((sourceFile) => {
        setPreSetValues({
          ...defaultValues,
          ...data,
          source_file: data?.source_file?.id ? data.source_file : sourceFile,
          start_date: data?.start_date ? moment(data?.start_date).toDate() : null,
          end_date: data?.end_date ? moment(data?.end_date).toDate() : null,
          exercise_date: data?.exercise_date ? moment(data?.exercise_date).toDate() : null,
          option_state_date: data?.option_state_date ? moment(data?.option_state_date).toDate() : null,
          premium: padDerivativePrice(data?.premium),
          premium_total: padDerivativePrice(data?.premium_total),
          other_expenses: padDerivativePrice(data?.other_expenses),
          strike_price: padDerivativePrice(data?.strike_price),
          exercise_price: padDerivativePrice(data?.exercise_price),
          termination_price: padDerivativePrice(legacySettlement.termination_price),
          receives: padDerivativePrice(legacySettlement.receives),
          termination_fee: data?.termination_fee ?? data?.termination_price ?? 0,
        });

        if (Number(data?.asset_type) === 2) {
          const market = data?.asset?.stock_market || data?.stock_market;
          if (market) {
            setStockMarket(market);
            getBasicDataObjects({ stock_market: market });
          }
        }
      });
    }).finally(() => {
      setLoading(false);
    })
    
  }
  
  
  useEffect(() => {
    if (objectId && parseInt(objectId) > -1) {
      getObject(objectId)
    }
    // Reset termination result whenever a different option is opened.
    setTerminationResult(null);
  }, [objectId, modalOpen])
  
  
  if (loading) {
    return (
      <Container fluid>
        <Spinner />
      </Container>
    )
  }

  return (
    <OptionEditFormik 
      t={t} 
      history={history} 
      preSetValues={preSetValues} 
      notify={notify} 
      objectId={objectId} 
      portfolio={portfolio}
      options={options}
      stockMarket={stockMarket}
      setStockMarket={setStockMarket}
      getBasicDataObjects={getBasicDataObjects}
      terminationResult={terminationResult}
      onTerminationResult={setTerminationResult}
      {...rest} 
    />
  )
  

}
  

export default OptionEditView;
